// react
import React, { useEffect, useState } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Grid, Dialog, DialogContent, DialogTitle, DialogActions, Button, Radio, RadioGroup, FormControlLabel, FormLabel, FormControl, Typography, Tooltip, IconButton, useTheme } from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

// record-controller
import fetchRecordsByType from "record-controller/fetchRecordsByType";

// utils
import exportCSV from "utils/exportCSV";

// A ---------------------------------------------------------------------- M

const ExportCsvDialog = ({ tag, open, handleClose, isTagGroupMember }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [magnitudes, setMagnitudes] = useState([]);
  const [selectedMagnitude, setSelectedMagnitude] = useState("");
  const [startDate, setStartDate] = useState(new Date(new Date().getTime() - 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());

  useEffect(() => {
    const fetchData = async () => {
      const records = await fetchRecordsByType(tag, "mps", isTagGroupMember, startDate, endDate);
      const magnitudeRecords = records.reduce((acc, record) => {
        const { magnitude } = record.data;
        acc[magnitude] = [...(acc[magnitude] || []), record].sort((a, b) => a.data.timestamp - b.data.timestamp);
        return acc;
      }, {});

      if (Object.keys(magnitudeRecords).length !== 0) {
        setMagnitudes(Object.keys(magnitudeRecords));
        setSelectedMagnitude(Object.keys(magnitudeRecords)[0]);
      } else {
        setMagnitudes([]);
        setSelectedMagnitude("");
      }
    };

    fetchData();
  }, [startDate, endDate]);

  const handleChange = (event) => {
    setSelectedMagnitude(event.target.value);
  };

  const handleExportCSV = async () => {
    const records = await fetchRecordsByType(tag, "mps", isTagGroupMember, startDate, endDate);
    const csvArray = records.map((record) => {
      return {
        id: record.id,
        timestamp: record.data.timestamp,
        value: record.data.value,
        uom: record.data.uom,
      };
    });
    exportCSV(csvArray.reverse());
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" fontWeight={700}>
              {t("export")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t("close")} placement="top">
              <IconButton onClick={handleClose} color="error" edge="end">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker label={t("start_date")} value={startDate} onChange={(date) => setStartDate(date)} />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker label={t("end_date")} value={endDate} onChange={(date) => setEndDate(date)} />
            </LocalizationProvider>
          </Grid>
          {magnitudes.length !== 0 && (
            <Grid item xs={12} sm={4}>
              <FormControl variant="standard" sx={{ m: 1, minWidth: 150 }}>
                <FormLabel>{t("select_parameter")}</FormLabel>
                <RadioGroup value={selectedMagnitude} onChange={handleChange}>
                  {magnitudes.map((magnitude) => {
                    return <FormControlLabel key={magnitude} value={magnitude} control={<Radio />} label={magnitude} />;
                  })}
                </RadioGroup>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="text"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleClose}
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleExportCSV}
          disabled={selectedMagnitude === ""}
        >
          {t("export")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportCsvDialog;
